/**
 * Tracks + owners data hooks backed by TanStack Query.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Track } from "@memes/shared";
import { ownersApi, tracksApi } from "../lib/api";

const TRACKS_KEY = ["tracks"] as const;
const OWNERS_KEY = ["owners"] as const;

export function useTracks() {
  return useQuery<Track[]>({
    queryKey: TRACKS_KEY,
    queryFn: () => tracksApi.list(),
  });
}

export function useOwners() {
  return useQuery({
    queryKey: OWNERS_KEY,
    queryFn: () => ownersApi.list().then((r) => r.owners),
  });
}

export function useUploadTrack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ owner, file }: { owner: string; file: File }) =>
      tracksApi.upload(owner, file),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TRACKS_KEY });
      qc.invalidateQueries({ queryKey: OWNERS_KEY });
    },
  });
}

export function useDeleteTrack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => tracksApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: TRACKS_KEY }),
  });
}

export function useDeleteByOwner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (owner: string) => tracksApi.removeByOwner(owner),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TRACKS_KEY });
      qc.invalidateQueries({ queryKey: OWNERS_KEY });
    },
  });
}

export function useDeleteAllTracks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => tracksApi.removeAll(),
    onSuccess: () => qc.invalidateQueries({ queryKey: TRACKS_KEY }),
  });
}

export function useMarkPlayed() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ts }: { id: number; ts?: number }) => tracksApi.markPlayed(id, ts),
    onSuccess: () => qc.invalidateQueries({ queryKey: TRACKS_KEY }),
  });
}
